import React, { useEffect, useState } from "react";
import {
  StyledColumnContainer,
  StyledBodyFinalInterview,
  StyledFinalInterviewInfo,
  StyledDevelopmentQuadrantContainer,
  StyledColumn,
} from "./FinalInterview.styled";
import {
  BodyTyp,
  TitlesTyp,
} from "../../../../../components/Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import {
  ACHIEVEMENTS_GOALS_LABEL,
  DEVELOPMENT_SCREEN_LABEL,
  EMPTY_DEVELOPMENT_SCREEN_LABEL,
  EMPTY_FINAL_INTERVIEW_MESSAGE,
  EMPTY_IMPACT_LABEL,
  EMPTY_PERCENTAGE_LABEL,
  EMPTY_STRING,
  FEEDBACK_FOR_COLLABORATORATOR_LABEL,
  FILL_IN_LABEL,
  FINAL_INTERVIEW_PC_LABEL,
  IMPACT_LABEL,
  PERCENTAGE_LABEL,
  TOTAL_LABEL,
} from "../../../../../constants/Dictionary";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import { theme } from "../../../../../constants/StyledComponents";
import { CHARACTER_MAX_LENGTH } from "../../../../../constants/AppConstants";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import TextArea from "../../../../../components/core/TextArea/TextArea";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { Loader } from "../../../../../components/core/Loader/Loader";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "../../../../../components/core/FormField/FormField";
import { TextBox } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { ReactComponent as QuadrantIcon } from "../../../../../icons/quadrant.svg";
import { ReactComponent as TargetIcon } from "../../../../../icons/target.svg";
import { ReactComponent as ImpactIcon } from "../../../../../icons/impact.svg";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import finalInterview from "../../../../../images/interview.png";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { ReactComponent as FillInIcon } from "../../../../../icons/fillin.svg";
import { IMPACT_RANGES } from "../../../../../constants/PotentialMatrixConstants";
import {
  useCmSheetFinalInterview,
  useUpdateCmSheetFinalInterview,
} from "../../../../../hooks/cmSheet/finalInterview.hook";
import { UpdateCollaboratorsCmSheetFinalInterviewRequest } from "../../../../../types/api/collaborators.type";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";

type FinalInterviewInfoProps = {
  title: string | null;
  description: string;
  label?: string | null;
  withBackground?: boolean;
  icon: React.ReactNode;
};

export const FinalInterviewInfo = ({
  title,
  description,
  label,
  withBackground = true,
  icon,
}: FinalInterviewInfoProps) => {
  const hasLabel = label !== undefined && label !== null;
  return (
    <StyledFinalInterviewInfo $withBackground={withBackground}>
      {title ? (
        <TitlesTyp
          variant="1"
          fontWeight={FontWeightEnum.SEMIBOLD}
          color={theme.colors.purple}
        >
          {title}
        </TitlesTyp>
      ) : (
        <div className="final-interview-label-container icon">
          <IconManager size={IconSizeEnum.MEDIUM} color={theme.colors.purple}>
            {icon}
          </IconManager>
        </div>
      )}
      <BodyTyp variant="3" fontWeight={FontWeightEnum.REGULAR}>
        {description}
      </BodyTyp>
      {hasLabel && (
        <div className="final-interview-label-container">
          <BodyTyp
            fontWeight={FontWeightEnum.REGULAR}
            color={theme.colors.purple}
          >
            {label}
          </BodyTyp>
        </div>
      )}
    </StyledFinalInterviewInfo>
  );
};

type FormData = {
  proposedComment?: string;
};

export const FinalInterview = ({
  footer,
  cmSheetInfo,
  isMyProfile,
  cmSheetStateMachineInfo,
  showFooter,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  )?.config;

  const { data, isFetching } = useCmSheetFinalInterview(cmSheetInfo.id);

  const { updateFinalInterview, isUpdating } = useUpdateCmSheetFinalInterview(
    cmSheetInfo.id,
  );

  const schema = yup
    .object({
      proposedComment: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const body: UpdateCollaboratorsCmSheetFinalInterviewRequest = {
        proposedComment: data.proposedComment ?? "",
      };
      await updateFinalInterview(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  const isEmpty = data == null;

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      reset({
        proposedComment: data?.proposedComment ?? "",
      });

      if (data && hasData === null) {
        setHasData(!isEmpty);
      }
    }
  }, [data, selectedYearConfig, hasData, edit, isFetching]);

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [CmSheetStateEnum.INTERMEDIATE_EVALUATION].includes(
          cmSheetStateMachineInfo.currentState,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="finalInterviewForm"
        disabledEditButton={!isDirty || isUpdating || isFetching}
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        showFooter={showFooter}
        setEdit={setEdit}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    isUpdating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  const percentageAchievementsGoals =
    selectedYearConfig?.finalInterviewDimensions?.find(
      (item) => item.id === data?.dimensionId,
    );

  const totalImpact = data?.totalImpact ?? null;
  const impactRange = IMPACT_RANGES.find(
    (range) =>
      totalImpact !== null &&
      totalImpact >= range.min &&
      totalImpact <= range.max,
  );

  const canViewDevelopmentScreen = () => {
    if (!isMyProfile) {
      return true;
    }

    return false;
  };

  return (
    <Loader loading={showLoader}>
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_FINAL_INTERVIEW_MESSAGE : ""}
            imageSrc={finalInterview}
          >
            {canEdit() && (
              <Button
                label={FILL_IN_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<FillInIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={isUpdating}>
          <StyledBodyFinalInterview>
            <StyledColumnContainer>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                {DEVELOPMENT_SCREEN_LABEL}
              </BodyTyp>
              <StyledColumn
                $canViewDevelopmentScreen={canViewDevelopmentScreen()}
              >
                {canViewDevelopmentScreen() && (
                  <>
                    <FinalInterviewInfo
                      title={data?.developmentScreen?.screen ?? null}
                      description={
                        data?.developmentScreen?.description ??
                        EMPTY_DEVELOPMENT_SCREEN_LABEL
                      }
                      label={
                        data?.developmentScreen?.range &&
                        `Range ${data?.developmentScreen?.range[0]} - ${data?.developmentScreen?.range[1]}`
                      }
                      withBackground={false}
                      icon={<QuadrantIcon />}
                    />
                    {data?.developmentScreen && (
                      <>
                        {data?.developmentScreen?.indicators?.map(
                          (item, index, indicators) => (
                            <StyledDevelopmentQuadrantContainer
                              key={index}
                              $bordered
                              $isFirst={index === 0}
                              $isLast={index === indicators.length - 1}
                            >
                              <BodyTyp variant="2">
                                {item.microCriterio}
                              </BodyTyp>
                              <BodyTyp
                                fontWeight={FontWeightEnum.SEMIBOLD}
                                variant="1"
                              >
                                {item.total}
                              </BodyTyp>
                            </StyledDevelopmentQuadrantContainer>
                          ),
                        )}
                        <StyledDevelopmentQuadrantContainer $bordered={false}>
                          <BodyTyp
                            variant="1"
                            fontWeight={FontWeightEnum.SEMIBOLD}
                          >
                            {TOTAL_LABEL}
                          </BodyTyp>
                          <BodyTyp
                            variant="1"
                            fontWeight={FontWeightEnum.SEMIBOLD}
                            color={theme.colors.purple}
                          >
                            {data?.developmentScreen?.totalIndicators}
                          </BodyTyp>
                        </StyledDevelopmentQuadrantContainer>
                      </>
                    )}
                  </>
                )}
              </StyledColumn>
              <TextBox
                label={FEEDBACK_FOR_COLLABORATORATOR_LABEL}
                text={data?.feedbackToCollaborator ?? EMPTY_STRING}
              />
            </StyledColumnContainer>
            <StyledColumnContainer>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                {ACHIEVEMENTS_GOALS_LABEL}
              </BodyTyp>
              <StyledColumn
                $canViewDevelopmentScreen={canViewDevelopmentScreen()}
              >
                <FinalInterviewInfo
                  title={
                    data?.dimensionPercentage
                      ? `${data?.dimensionPercentage}%`
                      : null
                  }
                  description={
                    data?.dimensionPercentage
                      ? PERCENTAGE_LABEL
                      : EMPTY_PERCENTAGE_LABEL
                  }
                  label={
                    data?.dimensionId
                      ? percentageAchievementsGoals?.initials
                      : EMPTY_STRING
                  }
                  icon={<TargetIcon />}
                />
                <FinalInterviewInfo
                  title={isMyProfile ? null : (totalImpact?.toString() ?? null)}
                  description={totalImpact ? IMPACT_LABEL : EMPTY_IMPACT_LABEL}
                  label={impactRange?.label}
                  icon={<ImpactIcon />}
                />
              </StyledColumn>
              {edit ? (
                <form id="finalInterviewForm" onSubmit={handleSubmit(onSubmit)}>
                  <FormField error={errors.proposedComment?.message}>
                    <Controller
                      name="proposedComment"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label={FINAL_INTERVIEW_PC_LABEL}
                          maxLength={CHARACTER_MAX_LENGTH}
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </form>
              ) : (
                <TextBox
                  label={FINAL_INTERVIEW_PC_LABEL}
                  text={data?.proposedComment ?? EMPTY_STRING}
                />
              )}
            </StyledColumnContainer>
          </StyledBodyFinalInterview>
        </Loader>
      </StyledHideOrShowContainer>
    </Loader>
  );
};
